// The library in numbers, on the home page (Oct 2026, user: "I love the
// statistical info… we have this already but it's hidden away… bring it back
// and add to the home page"). The statistics cover every tune the app can
// open and come precomputed (public/library-stats.json); every bar and every
// key opens the tunes behind it, in place, so the numbers double as a way to
// browse. Nothing on screen names the collections the tunes come from (user
// ruling, Oct 2026).

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLibraryStats } from '@/data/libraryStats';
import type { LibraryStats as Stats, LibraryStatsGroup } from '@/music/libraryStats';
import { APP_ROUTES } from '@/navigation';
import { homeView, type LibrarySelection as Selection } from './homeView';
// Distinguish the component from keyWheel.ts on case-insensitive filesystems.
import { KeyWheel } from './KeyWheel.tsx';
import { tuneHref } from './tuneHref';

const fmt = (n: number) => n.toLocaleString('en-GB');
const pct = (share: number) => `${Math.round(share * 100)}%`;
/** How many tunes a list shows before "Show all". */
const FIRST = 12;

function TuneList({ stats, selection }: { stats: Stats; selection: Selection }) {
  // A list remounts per selection, so it starts collapsed unless this is the
  // one the page kept open (homeView), as it was left.
  const [all, setAll] = useState(() => homeView.open === selection && homeView.all);
  const shown = all ? selection.tunes : selection.tunes.slice(0, FIRST);
  return (
    <div
      className="home-library-tunes"
      id={`library-${selection.card}-tunes`}
      data-testid="library-tunes"
    >
      <h4>{selection.heading}</h4>
      <ul className={all ? 'is-all' : undefined}>
        {shown.map((index) => {
          const tune = stats.tunes[index];
          return (
            <li key={index}>
              <Link to={tuneHref(tune.t, tune.c, tune.rb ? 'realbook' : undefined)}>{tune.t}</Link>
              {tune.c && <span className="muted"> · {tune.c}</span>}
              {tune.m && (
                <span className="tune-melody" title="Has a written melody" aria-label="has melody">
                  ♪
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {!all && selection.tunes.length > FIRST && (
        <button
          type="button"
          className="btn btn-sm"
          data-testid="library-show-all"
          onClick={() => {
            setAll(true);
            homeView.all = true;
          }}
        >
          Show all {fmt(selection.tunes.length)}
        </button>
      )}
    </div>
  );
}

function BarCard({
  card,
  title,
  rows,
  open,
  onToggle,
  children,
}: {
  card: string;
  title: string;
  rows: LibraryStatsGroup[];
  open: Selection | null;
  onToggle: (selection: Selection) => void;
  children?: ReactNode;
}) {
  const max = Math.max(1, ...rows.map((row) => row.tunes.length));
  return (
    <section
      className="corpus-card home-library-card"
      data-card={card}
      data-testid={`library-card-${card}`}
      data-scroll-anchor={`library-card-${card}`}
    >
      <h3>{title}</h3>
      <div className="bar-list">
        {rows.map((row) => {
          const expanded = open?.card === card && open.label === row.label;
          return (
            <button
              key={row.label}
              type="button"
              className="bar-row is-action"
              aria-expanded={expanded}
              aria-controls={expanded ? `library-${card}-tunes` : undefined}
              title={`${row.label}: ${fmt(row.tunes.length)} tunes`}
              onClick={() =>
                onToggle({ card, label: row.label, heading: row.label, tunes: row.tunes })
              }
            >
              <span className="bar-label">{row.label}</span>
              <span className="bar-track">
                <span
                  className="bar-fill"
                  style={{ width: `${(100 * row.tunes.length) / max}%` }}
                />
              </span>
              <span className="bar-value">{fmt(row.tunes.length)}</span>
            </button>
          );
        })}
      </div>
      {children}
    </section>
  );
}

export function LibraryStats() {
  const stats = useLibraryStats();
  const [open, setOpen] = useState<Selection | null>(() => homeView.open);
  if (!stats) return null;

  const toggle = (next: Selection) => {
    const opening = !(open?.card === next.card && open.label === next.label);
    homeView.open = opening ? next : null;
    homeView.all = false;
    setOpen(homeView.open);
  };
  const panel = (card: string) =>
    open?.card === card ? <TuneList key={open.label} stats={stats} selection={open} /> : null;
  const { totals } = stats;
  const tiles: [string, string][] = [
    [fmt(totals.tunes), 'tunes'],
    [fmt(totals.composers), 'composers'],
    [fmt(totals.chords), 'chords'],
    // A word joiner after each dash, so "ii–V" never breaks across lines.
    [pct(totals.iiVShare), 'of changes are a ii–⁠V'],
    [pct(totals.iiVITuneShare), 'of tunes resolve a ii–⁠V–⁠I'],
    [pct(totals.minorShare), 'in a minor key'],
  ];
  const rootMotion = stats.rootMotion.slice(0, 6);
  const rootMax = Math.max(1, ...rootMotion.map((row) => row.count));

  return (
    <section
      className="home-library"
      aria-labelledby="home-library-heading"
      data-testid="home-library"
    >
      <h2 id="home-library-heading">The library</h2>
      <div className="corpus-tiles" data-testid="library-tiles" data-scroll-anchor="library-tiles">
        {tiles.map(([value, label]) => (
          <div key={label} className="corpus-tile">
            <div className="corpus-tile-value">{value}</div>
            <div className="corpus-tile-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="home-library-grid">
        <div className="home-library-col">
          <section
            className="corpus-card home-library-card"
            data-card="keys"
            data-testid="library-card-keys"
            data-scroll-anchor="library-card-keys"
          >
            <h3>Keys</h3>
            <KeyWheel
              keys={stats.keys}
              selected={open?.card === 'keys' ? open.label : null}
              onSelect={(slot) =>
                toggle({ card: 'keys', label: slot.name, heading: slot.name, tunes: slot.tunes })
              }
            />
            {panel('keys')}
          </section>
          <BarCard
            card="composers"
            title="Composers"
            rows={stats.composers.slice(0, 10)}
            open={open}
            onToggle={toggle}
          >
            {panel('composers')}
          </BarCard>
          <section
            className="corpus-card home-library-card"
            data-card="motion"
            data-testid="library-card-motion"
            data-scroll-anchor="library-card-motion"
          >
            <h3>Root motion</h3>
            <div className="bar-list">
              {rootMotion.map((row) => (
                <div key={row.label} className="bar-row" title={`${row.label}: ${fmt(row.count)}`}>
                  <span className="bar-label">{row.label}</span>
                  <span className="bar-track">
                    <span
                      className="bar-fill"
                      style={{ width: `${(100 * row.count) / rootMax}%` }}
                    />
                  </span>
                  <span className="bar-value">{fmt(row.count)}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
        <div className="home-library-col">
          <BarCard
            card="feels"
            title="Feels"
            rows={stats.feels.slice(0, 8)}
            open={open}
            onToggle={toggle}
          >
            {panel('feels')}
          </BarCard>
          <BarCard
            card="forms"
            title="Forms"
            rows={stats.forms.slice(0, 8)}
            open={open}
            onToggle={toggle}
          >
            {panel('forms')}
          </BarCard>
          <BarCard
            card="progressions"
            title="Progressions"
            rows={stats.progressions.slice(0, 6)}
            open={open}
            onToggle={toggle}
          >
            {panel('progressions')}
          </BarCard>
          <section
            className="corpus-card home-library-card"
            data-card="adventurous"
            data-testid="library-card-adventurous"
            data-scroll-anchor="library-card-adventurous"
          >
            <h3>Least predictable changes</h3>
            <ol className="home-library-league">
              {stats.leagues.adventurous.slice(0, 5).map(({ tune: index }) => {
                const tune = stats.tunes[index];
                return (
                  <li key={index}>
                    <Link to={tuneHref(tune.t, tune.c, tune.rb ? 'realbook' : undefined)}>
                      {tune.t}
                    </Link>
                    {tune.c && <span className="muted"> · {tune.c}</span>}
                  </li>
                );
              })}
            </ol>
          </section>
        </div>
      </div>
      <Link className="btn home-library-more" to={APP_ROUTES.corpus}>
        All statistics
      </Link>
    </section>
  );
}
